import { logUsage } from 'owa-analytics';
import type {
    UTCalendarEventFeatureAction,
    UTCalendarUIContext,
    UTCalendarOrigin,
} from 'owa-unified-telemetry';
import { type UTCalendarFeatureName } from 'owa-unified-telemetry';
import { flattenTelemetryData } from './flattenTelemetryData';

/**
 * Logs unified telemetry for mail category actions
 * @param action The category action being performed
 * @param uiContext The UI context where the action occurred
 * @param origin The origin of the action
 * @param success Whether the action was successful (defaults to true)
 * @param failureInfo Optional error message if the action failed
 * @param additionalData Optional additional data to include in telemetry
 */
export function logMailCategorizeTelemetry(
    action: UTCalendarEventFeatureAction,
    uiContext: UTCalendarUIContext,
    origin: UTCalendarOrigin,
    success: boolean = true,
    failureInfo?: string
): void {
    const data = {
        FeatureName: 'Categorize',
        FeatureAction: action,
        FeatureUIContext: uiContext,
        Origin: origin,
        ActionSuccess: success,
        ...(failureInfo && { ActionFailureInfo: failureInfo }),
    };

    // Flatten nested objects in data to one level
    const dataFlattened = flattenTelemetryData(data);

    const unifiedTelemetryData = {
        eventName: 'CalendarEventUserAction' as const,
        bypassEventMatchingCheck: true,
        data,
    };

    /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
     * BASELINE: DO NOT COPY/PASTE!
     *	> Argument at index 1 for logUsage must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
    logUsage('CalendarEventUserAction', undefined, dataFlattened as any, {
        unifiedTelemetry: unifiedTelemetryData,
    });
}

/**
 * Creates success/error callbacks for mail category operations with telemetry logging
 * @param action The category action being performed
 * @param uiContext The UI context where the action occurred
 * @param origin The origin of the action
 */
export function getMailCategorizeCallbacks(
    action: UTCalendarEventFeatureAction,
    uiContext: UTCalendarUIContext,
    origin: UTCalendarOrigin
): {
    onSuccess: () => void;
    onError: (error: Error) => void;
} {
    return {
        onSuccess: (): void => {
            logMailCategorizeTelemetry(action, uiContext, origin, true);
        },
        onError: (error: Error): void => {
            logMailCategorizeTelemetry(action, uiContext, origin, false, error.message);
        },
    };
}
